/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { SessionGateService } from '@sa/core/services/session-gate.service';
import { environment } from '@sa/environments/environment';

@Injectable()
export class CustomHeadersInterceptor implements HttpInterceptor {
  private sessionGate = inject(SessionGateService);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Fix 16.10: use sessionStorage so the correlation ID does not persist across browser sessions
    const correlationId =
      sessionStorage.getItem('correlationId') || this.generateCorrelationId();
    sessionStorage.setItem('correlationId', correlationId);

    const sessionId =
      localStorage.getItem('sessionId') || this.generateCorrelationId();
    localStorage.setItem('sessionId', sessionId);

    const requestWithHeaders = request.clone({
      setHeaders: {
        'x-correlation-id': correlationId,
        'X-Session-Id': sessionId,
        'X-Client-Type': 'web',
      },
    });

    // Only gate calls to the main backend API. Requests to external services (e.g. Graph,
    // Function Apps) should not be blocked by the session-reset flow.
    const isBackendRequest = request.url.startsWith(environment.baseUrl);
    if (!isBackendRequest) {
      return next.handle(requestWithHeaders);
    }

    // DELETE /session is the reset call itself — let it through immediately so
    // it can clear the stale Redis key and unblock the gate for everything else.
    const isSessionReset =
      request.method === 'DELETE' &&
      request.url === `${environment.baseUrl}/session`;

    if (isSessionReset) {
      return next.handle(requestWithHeaders);
    }

    // All other backend requests wait until the session gate is open
    // (i.e. resetSessionKey() has completed and the stale Redis key is gone).
    return this.sessionGate.waitForReady().pipe(
      switchMap(() => next.handle(requestWithHeaders))
    );
  }

  private generateCorrelationId(): string {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);

    // RFC 4122 version 4 UUID bits
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
}
